// frontend/src/pages/UserManagement.js
import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Spinner, Alert } from 'react-bootstrap';
import { getUsers, createUser, updateUser, deleteUser } from '../api';
import UserFormModal from '../components/forms/UserFormModal';
import DeleteConfirmationModal from '../components/forms/DeleteConfirmationModal';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingUser, setDeletingUser] = useState(null);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await getUsers();
            setUsers(data);
        } catch (err) {
            setError('Impossibile caricare gli utenti.');
            console.error("Failed to fetch users:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleShowCreateModal = () => {
        setEditingUser(null);
        setShowModal(true);
    };

    const handleShowEditModal = (user) => {
        setEditingUser(user);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingUser(null);
    };

    const handleShowDeleteModal = (user) => {
        setDeletingUser(user);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setDeletingUser(null);
    };

    const handleDeleteUser = async () => {
        if (!deletingUser) return;
        try {
            await deleteUser(deletingUser.id);
            handleCloseDeleteModal();
            fetchUsers();
        } catch (err) {
            console.error("Failed to delete user:", err);
            setError(err.response?.data?.detail || 'Errore durante l\'eliminazione dell\'utente.');
        }
    };

    const handleFormSubmit = async (userData) => {
        try {
            if (editingUser) {
                await updateUser(editingUser.id, userData);
            } else {
                await createUser(userData);
            }
            handleCloseModal();
            fetchUsers();
        } catch (err) {
            console.error("Failed to save user:", err);
            let errorMessage = editingUser ? 'Errore durante l\'aggiornamento dell\'utente.' : 'Errore durante la creazione dell\'utente.';
            if (err.response?.data?.detail) {
                if (Array.isArray(err.response.data.detail)) {
                    errorMessage = err.response.data.detail.map(e => e.msg).join(', ');
                } else {
                    errorMessage = err.response.data.detail;
                }
            }
            setError(errorMessage);
        }
    };

    return (
        <Container>
            <h1 className="my-4">Gestione Utenti</h1>
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            <Button variant="primary" className="mb-3" onClick={handleShowCreateModal}>
                Crea Nuovo Utente
            </Button>
            
            <UserFormModal
                show={showModal}
                handleClose={handleCloseModal}
                handleSubmit={handleFormSubmit}
                initialData={editingUser}
            />

            <DeleteConfirmationModal
                show={showDeleteModal}
                handleClose={handleCloseDeleteModal}
                handleConfirm={handleDeleteUser}
                message={`Sei sicuro di voler eliminare l'utente ${deletingUser?.email}?`}
            />

            {loading ? (
                <Spinner animation="border" />
            ) : (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Email</th>
                            <th>Stato</th>
                            <th>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.id}>
                                <td>{user.id}</td>
                                <td>{user.email}</td>
                                <td>
                                    {user.is_active ? (
                                        <span className="badge bg-success">Attivo</span>
                                    ) : (
                                        <span className="badge bg-danger">Inattivo</span>
                                    )}
                                    {user.is_staff && <span className="badge bg-primary ms-1">Staff</span>}
                                </td>
                                <td>
                                    <Button variant="warning" size="sm" className="me-2" onClick={() => handleShowEditModal(user)}>
                                        Modifica
                                    </Button>
                                    <Button variant="danger" size="sm" onClick={() => handleShowDeleteModal(user)}>
                                        Elimina
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </Container>
    );
};

export default UserManagement;
